import { useCallback, useEffect, useState } from "react";

import { APP_CONFIG_QUERY_KEY } from "~/features/permissions/AbilityContext";
import { DEVICE_TOKEN_QUERY_KEY } from "~/features/push-notifications/use-native-subscription";
import { PUSH_SUBSCRIPTION_QUERY_KEY } from "~/features/push-notifications/use-shared-push-utils";

import type { QueryKey } from "@tanstack/react-query";
import {
  useIsFetching,
  useIsRestoring,
  useQueryClient,
} from "@tanstack/react-query";

const IGNORED_QUERY_KEYS = [
  PUSH_SUBSCRIPTION_QUERY_KEY,
  DEVICE_TOKEN_QUERY_KEY,
  APP_CONFIG_QUERY_KEY,
];

export const useQueryLastUpdated = (queryKeys?: QueryKey[]) => {
  const queryClient = useQueryClient();
  const numberOfFetchingQueries = useIsFetching();

  const numberOfRelevantFetchingQueries = queryKeys?.reduce<number>(
    (numberOfFetchingQueries, queryKey) => {
      return (
        numberOfFetchingQueries +
        queryClient.isFetching({
          queryKey,
        })
      );
    },
    0,
  );

  const [lastUpdated, setLastUpdated] = useState<Date>();
  const [error, setError] = useState<Error>();
  const [isFetching, setIsFetching] = useState(false);

  // true while PersistQueryClientProvider is restoring from storage,
  // false once restore is complete. Used to re-trigger the effect below
  // so we read dataUpdatedAt after persistence hydration (e.g. offline cold start).
  const isRestoring = useIsRestoring();

  useEffect(() => {
    if (numberOfRelevantFetchingQueries === 0) {
      setIsFetching(false);
      const queries = (queryKeys ?? []).flatMap((queryKey) =>
        queryClient
          .getQueriesData({
            type: "active",
            queryKey,
            predicate: ({ queryKey }) =>
              !IGNORED_QUERY_KEYS.some((ignoredKey) =>
                queryKey.includes(ignoredKey),
              ),
          })
          .map(([query]) => queryClient.getQueryState(query)),
      );

      const { latestSuccessfulQuery, latestFailedQuery } = queries.reduce<{
        latestSuccessfulQuery?: (typeof queries)[number];
        latestFailedQuery?: (typeof queries)[number];
      }>((acc, query) => {
        const dataUpdatedAt = query?.dataUpdatedAt || 0;
        const errorUpdatedAt = query?.errorUpdatedAt || 0;
        if (dataUpdatedAt > (acc.latestSuccessfulQuery?.dataUpdatedAt || 0)) {
          acc.latestSuccessfulQuery = query;
        }

        if (errorUpdatedAt > (acc.latestFailedQuery?.errorUpdatedAt || 0)) {
          acc.latestFailedQuery = query;
        }

        return acc;
      }, {});
      if (latestSuccessfulQuery?.dataUpdatedAt) {
        setLastUpdated(new Date(latestSuccessfulQuery.dataUpdatedAt));
      }

      if (
        (latestFailedQuery?.errorUpdatedAt || 0) >=
        (latestSuccessfulQuery?.dataUpdatedAt || 0)
      ) {
        setError(
          latestFailedQuery?.error ? latestFailedQuery?.error : undefined,
        );
      } else {
        setError(undefined);
      }
    } else {
      const queries = (queryKeys ?? []).flatMap((queryKey) =>
        queryClient.getQueriesData({
          type: "active",
          fetchStatus: "fetching",
          queryKey,
          predicate: ({ queryKey }) =>
            !IGNORED_QUERY_KEYS.some((ignoredKey) =>
              queryKey.includes(ignoredKey),
            ),
        }),
      );
      setIsFetching(queries.length > 0);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    // Need to this value from useIsFetching just to trigger the effect. Since queryClient.isFetching is not triggering the effect by itself.
    numberOfFetchingQueries,
    numberOfRelevantFetchingQueries,
    queryClient,
    isRestoring,
    // eslint-disable-next-line react-hooks/exhaustive-deps
    queryKeys?.flat().join(),
  ]);

  const refetchPassedQueryKeys = useCallback(
    () =>
      queryKeys?.forEach((queryKey) =>
        queryClient.refetchQueries({
          queryKey,
        }),
      ),
    [queryClient, queryKeys],
  );

  return { lastUpdated, error, isFetching, refetchPassedQueryKeys };
};
